import React, { useState } from 'react';
import api from '../api';

// Verejná samoregistrácia (Free plán). Po odoslaní vznikne PENDING firma —
// super-admin ju aktivuje a klient dostane e‑mail s odkazom na nastavenie hesla.
type Candidate = { ico: string; name: string; address_street?: string|null; address_city?: string|null; address_zip?: string|null };

export default function Register() {
  const [form, setForm] = useState({
    ico: '', companyName: '', adminFirstName: '', adminLastName: '', adminEmail: '', phone: '',
  });
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [address, setAddress] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupMsg, setLookupMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  const applyCompany = (c: Candidate) => {
    set('ico', c.ico || '');
    set('companyName', c.name || '');
    setAddress([c.address_street, c.address_zip, c.address_city].filter(Boolean).join(', '));
    setCandidates([]);
    setLookupMsg(c.name ? '✓ Firma načítaná' : '⚠ Firma nájdená, ale názov chýba — doplň ručne');
  };

  // Unified lookup: ICO pattern → priamy lookup, inak fulltext search v RPO.
  // Pri jedinom hite autofill; pri viacerých zobrazíme klikateľný zoznam.
  const lookup = async () => {
    const q = query.trim();
    if (q.length < 3) {
      setLookupMsg('✗ Zadaj IČO (6–8 číslic) alebo aspoň 3 znaky názvu');
      return;
    }
    setLookingUp(true);
    setLookupMsg('');
    setCandidates([]);
    try {
      const isIco = /^\d{6,8}$/.test(q.replace(/\s/g, ''));
      if (isIco) {
        const cleanIco = q.replace(/\s/g, '');
        const res = await api.get(`/auth/public/company-lookup/${cleanIco}`);
        applyCompany({
          ico: cleanIco, name: res.data?.name || '',
          address_street: res.data?.address_street, address_city: res.data?.address_city, address_zip: res.data?.address_zip,
        });
      } else {
        const res = await api.get(`/auth/public/company-search?q=${encodeURIComponent(q)}`);
        const list: Candidate[] = res.data || [];
        if (list.length === 0) { setLookupMsg('✗ Nenašlo sa nič — skús iný názov alebo IČO'); return; }
        if (list.length === 1) { applyCompany(list[0]); return; }
        setCandidates(list);
        setLookupMsg(`Nájdených ${list.length} firiem — vyber jednu:`);
      }
    } catch (err: any) {
      setLookupMsg('✗ ' + (err.response?.data?.error || 'Vyhľadávanie zlyhalo'));
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/auth/register', form);
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registráciu sa nepodarilo dokončiť.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img src="/mgtech-logo.png" alt="mG.tech" style={{ maxHeight: '90px', maxWidth: '280px', objectFit: 'contain', display: 'block', margin: '0 auto 0.75rem' }} />
          <h1 style={{ margin: 0, color: '#1f2937', fontSize: '1.35rem' }}>Registrácia – Free plán</h1>
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
            <p style={{ color: '#16a34a', fontWeight: 600, margin: '0 0 0.5rem' }}>Žiadosť sme prijali!</p>
            <p style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.5 }}>
              Po overení vám pošleme e‑mail s odkazom na nastavenie hesla a prístupom
              (zvyčajne do 24 hodín). Onboarding máte zdarma.
            </p>
            <a href="/login" style={{ ...btn, display: 'inline-block', textDecoration: 'none', marginTop: '0.5rem' }}>Späť na prihlásenie</a>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <label style={lbl}>IČO alebo názov firmy *</label>
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.25rem' }}>
                <input style={{ ...input, flex: 1 }} value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
                  placeholder="napr. 31322832 alebo Deliber" />
                <button type="button" onClick={lookup} disabled={lookingUp || !query.trim()}
                  style={{ ...btnNeutral, whiteSpace: 'nowrap', opacity: (lookingUp || !query.trim()) ? 0.6 : 1 }}>
                  {lookingUp ? 'Hľadám…' : '🔍 Načítať'}
                </button>
              </div>
              {lookupMsg && <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: lookupMsg.startsWith('✓') ? '#16a34a' : (lookupMsg.startsWith('⚠') ? '#d97706' : (lookupMsg.startsWith('Nájdených') ? '#374151' : '#dc2626')) }}>{lookupMsg}</p>}
              {candidates.length > 0 && (
                <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '0.5rem', maxHeight: '220px', overflowY: 'auto' }}>
                  {candidates.map((c, i) => (
                    <div key={i} onClick={() => applyCompany(c)}
                      style={{ padding: '0.5rem 0.6rem', cursor: 'pointer', borderBottom: i < candidates.length - 1 ? '1px solid #f3f4f6' : 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1f2937' }}>{c.name || '(bez názvu)'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        IČO {c.ico}{c.address_street ? ` · ${c.address_street}, ${c.address_city || ''}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <label style={lbl}>IČO *</label>
              <input style={inputReadOnly} required readOnly tabIndex={-1}
                value={form.ico} placeholder="Vyplní sa z vyhľadávania hore"
                title="Vyplnené automaticky z RPO" />

              <label style={lbl}>Názov firmy *</label>
              <input style={inputReadOnly} required readOnly tabIndex={-1}
                value={form.companyName} placeholder="Vyplní sa z vyhľadávania hore"
                title="Vyplnené automaticky z RPO" />
              {address && <p style={{ margin: '0.15rem 0 0.75rem', fontSize: '0.78rem', color: '#9ca3af' }}>{address}</p>}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Meno *</label>
                  <input style={input} required value={form.adminFirstName} onChange={e => set('adminFirstName', e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Priezvisko *</label>
                  <input style={input} required value={form.adminLastName} onChange={e => set('adminLastName', e.target.value)} />
                </div>
              </div>

              <label style={lbl}>E‑mail *</label>
              <input style={input} type="email" required value={form.adminEmail} onChange={e => set('adminEmail', e.target.value)} />

              <label style={lbl}>Telefón</label>
              <input style={input} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+421…" />

              <button type="submit" disabled={submitting}
                style={{ ...btn, width: '100%', marginTop: '0.5rem', opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                {submitting ? 'Odosielam…' : 'Odoslať žiadosť'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
              Už máte účet? <a href="/login" style={{ color: '#2563eb' }}>Prihláste sa</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const wrap: React.CSSProperties = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', padding: '1rem' };
const card: React.CSSProperties = { backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', width: '100%', maxWidth: '440px' };
const lbl: React.CSSProperties = { display: 'block', marginBottom: '0.25rem', marginTop: '0.5rem', color: '#374151', fontSize: '0.85rem' };
const input: React.CSSProperties = { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box' };
const btn: React.CSSProperties = { padding: '0.75rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer' };
const btnNeutral: React.CSSProperties = { padding: '0.5rem 0.9rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };
// Štýl pre needitovateľné polia (IČO, Názov) — vizuálne info, nie input.
const inputReadOnly: React.CSSProperties = { width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box', backgroundColor: '#f9fafb', color: '#1f2937', cursor: 'default' };
